
import React, { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle2, Loader2, Calendar, RefreshCw, XCircle, ShieldCheck, Zap, Share2, Check } from 'lucide-react';
import { getDenizConfirmation, generateProductImage } from '../services/geminiService';
import GTAAds from './GTAAds';

interface ResultViewProps {
  query: string;
  onBack: () => void;
}

type ReservationStatus = 'idle' | 'picking' | 'checking' | 'rejected';

const REJECTION_REASONS = [
  "De XLR-kabels hebben op die dag een collectieve burn-out.",
  "Helaas, de zwaartekracht in het magazijn is op die dag te hoog.",
  "De betreffende unit is gereserveerd voor een geheim uilen-feest.",
  "Deniz heeft besloten dat deze datum astrologisch onverantwoord is.",
  "Er is die dag een landelijk tekort aan gaffa-tape."
];

const ResultView: React.FC<ResultViewProps> = ({ query, onBack }) => {
  const [aiText, setAiText] = useState<string>('');
  const [productImage, setProductImage] = useState<string | null>(null);
  const [loadingText, setLoadingText] = useState(true);
  const [loadingImage, setLoadingImage] = useState(true);
  const [reservationStatus, setReservationStatus] = useState<ReservationStatus>('idle');
  const [rejectionData, setRejectionData] = useState<{reason: string, alternative: string} | null>(null);
  const [dates, setDates] = useState({ start: '', end: '' });
  const [isAiPro, setIsAiPro] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    // Veiligere check voor API key om Uncaught errors te voorkomen
    const checkApiKey = () => {
      try {
        const key = process.env.API_KEY;
        setIsAiPro(!!(key && key !== 'undefined' && key.trim().length > 5));
      } catch (e) {
        setIsAiPro(false);
      }
    };
    checkApiKey();

    // Dynamische title voor betere share preview
    const originalTitle = document.title;
    document.title = `Deniz verhuurt: ${query}!`;

    const fetchInfo = async () => {
      setLoadingText(true);
      const text = await getDenizConfirmation(query);
      setAiText(text);
      setLoadingText(false);
    };

    const fetchImage = async () => {
      setLoadingImage(true);
      const img = await generateProductImage(query);
      setProductImage(img);
      setLoadingImage(false);
    };

    fetchInfo();
    fetchImage();

    return () => {
      document.title = originalTitle;
    };
  }, [query]);

  const handleCheckAvailability = () => {
    if (!dates.start || !dates.end) {
      alert("Kies eerst de data!");
      return;
    }
    setReservationStatus('checking');
    setTimeout(() => {
      const reason = REJECTION_REASONS[Math.floor(Math.random() * REJECTION_REASONS.length)];
      setRejectionData({ reason, alternative: "Deniz' eigen lunchbox." });
      setReservationStatus('rejected');
    }, 1500);
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}${window.location.pathname}?q=${encodeURIComponent(query)}`;
    const shareText = `Kijk, Deniz verhuurt gewoon "${query}"! 🚀 Check het zelf op DenizVerhuurt.nl:`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Deniz Verhuurt Dit!',
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        // Alleen loggen als het geen 'AbortError' is (gebruiker die op 'annuleren' klikt)
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error("Deel-fout:", err);
        }
      }
    } else {
      // Fallback: Kopieer naar klembord
      try {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
      } catch (err) {
        // Fallback voor browsers zonder clipboard API of over HTTP
        const textArea = document.createElement("textarea");
        textArea.value = `${shareText} ${shareUrl}`;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 3000);
        } catch (e) {
          alert("Kopiëren mislukt. Kopieer de URL uit de adresbalk.");
        }
        document.body.removeChild(textArea);
      }
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center p-4 md:p-8 pb-20 text-center animate-in fade-in duration-500">
      <div className="max-w-4xl w-full flex justify-between items-center mb-6 md:mb-10">
        <button 
          onClick={onBack}
          className="flex items-center text-gray-500 hover:text-black transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 md:w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm md:text-base font-bold">Terug</span>
        </button>

        <button 
          onClick={handleShare}
          className={`flex items-center space-x-2 px-4 py-2 rounded-full border shadow-sm transition-all active:scale-95
            ${copySuccess ? 'bg-green-50 border-green-200 text-green-600 scale-105' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-400 hover:shadow-md'}`}
        >
          {copySuccess ? <Check className="w-4 h-4 animate-in zoom-in" /> : <Share2 className="w-4 h-4" />}
          <span className="text-xs font-bold uppercase tracking-tighter">
            {copySuccess ? 'Gekopieerd!' : 'Deel met groep'}
          </span>
        </button>
      </div>

      <div className="max-w-4xl w-full">
        <div className="mb-6 flex justify-center">
          <div className="bg-green-100 p-4 md:p-6 rounded-full animate-bounce shadow-inner">
            <CheckCircle2 className="w-10 h-10 md:w-16 h-16 text-green-500" />
          </div>
        </div>
        
        <h1 className="text-3xl md:text-6xl font-black mb-4 text-gray-900 leading-[1.1] tracking-tight">
          JA! <br />
          <span className="text-[#4285F4]">Deniz</span> verhuurt dit!
        </h1>

        <div className="text-lg md:text-2xl text-gray-500 font-medium mb-10 md:mb-16 italic px-4">
          "{query}" ligt voor je klaar.
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-stretch mb-12">
          {/* Afbeelding Sectie */}
          <div className="relative order-1 md:order-2">
            {loadingImage ? (
              <div className="aspect-video bg-gray-50 rounded-[2rem] flex flex-col items-center justify-center border-2 border-dashed border-gray-100 min-h-[250px]">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-2" />
                <span className="text-gray-400 text-[10px] font-black uppercase tracking-[0.2em]">Magazijn scan...</span>
              </div>
            ) : productImage ? (
              <div className="relative overflow-hidden rounded-[2rem] shadow-2xl border border-gray-200 group">
                <img 
                  src={productImage} 
                  alt={query} 
                  className="w-full aspect-video object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute top-4 right-4 flex items-center space-x-2 bg-black/60 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/20">
                  {isAiPro ? <Zap className="w-3 h-3 text-amber-400 fill-amber-400" /> : <ShieldCheck className="w-3 h-3 text-blue-400" />}
                  <span className="text-[10px] text-white font-black uppercase tracking-tighter">
                    {isAiPro ? 'Pro Gen' : 'Smart Scan'}
                  </span>
                </div>
              </div>
            ) : null}
          </div>

          {/* Tekst Sectie */}
          <div className="bg-gray-50 rounded-[2rem] p-6 md:p-10 border border-gray-100 shadow-inner flex flex-col justify-center min-h-[250px] order-2 md:order-1 relative overflow-hidden">
            {!isAiPro && <div className="absolute top-0 left-0 w-1.5 h-full bg-blue-500/30"></div>}
            
            {loadingText ? (
              <div className="flex flex-col items-center space-y-4 py-10">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                <p className="text-gray-400 italic text-sm">Deniz zoekt details...</p>
              </div>
            ) : (
              <div className="relative z-10">
                <p className="text-base md:text-xl text-gray-700 leading-relaxed font-bold italic">
                  "{aiText}"
                </p>
                <div className="mt-4 flex items-center justify-center space-x-2 opacity-30">
                  <div className="h-[1px] w-8 bg-gray-400"></div>
                  <span className="text-[10px] font-black uppercase tracking-widest">Deniz Personality Engine</span>
                  <div className="h-[1px] w-8 bg-gray-400"></div>
                </div>
              </div>
            )}
            
            {reservationStatus === 'idle' && !loadingText && (
              <div className="flex flex-col space-y-4 mt-8">
                <button 
                  className={`px-6 md:px-10 py-3 md:py-4 rounded-full text-base md:text-lg font-bold shadow-lg hover:shadow-xl transition-all active:scale-95 flex items-center justify-center text-white
                    ${isAiPro ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                  onClick={() => setReservationStatus('picking')}
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Datum Kiezen
                </button>
                <button 
                  onClick={handleShare}
                  className="text-gray-400 hover:text-gray-700 text-[10px] font-black uppercase tracking-widest transition-colors flex items-center justify-center p-2 group"
                >
                  <Share2 className="w-3 h-3 mr-2 group-hover:scale-110 transition-transform" />
                  Stuur bewijs naar je groepsapp
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Reserveringsformulier */}
        {reservationStatus !== 'idle' && (
          <div className="max-w-xl mx-auto bg-white border border-gray-100 rounded-[2.5rem] shadow-2xl overflow-hidden mb-20 animate-in slide-in-from-bottom-5 duration-300">
            <div className={`py-4 text-white font-bold flex items-center justify-center text-sm md:text-base ${isAiPro ? 'bg-amber-600' : 'bg-blue-600'}`}>
              <Calendar className="w-4 h-4 mr-2" />
              Check Beschikbaarheid
            </div>
            
            <div className="p-6 md:p-8">
              {reservationStatus === 'picking' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="text-left">
                      <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Vanaf</label>
                      <input 
                        type="date" 
                        className="w-full border border-gray-200 rounded-2xl p-4 outline-none focus:ring-4 focus:ring-blue-50 transition-all text-base font-bold"
                        value={dates.start}
                        onChange={(e) => setDates({...dates, start: e.target.value})}
                      />
                    </div>
                    <div className="text-left">
                      <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Tot</label>
                      <input 
                        type="date" 
                        className="w-full border border-gray-200 rounded-2xl p-4 outline-none focus:ring-4 focus:ring-blue-50 transition-all text-base font-bold"
                        value={dates.end}
                        onChange={(e) => setDates({...dates, end: e.target.value})}
                      />
                    </div>
                  </div>
                  <button 
                    onClick={handleCheckAvailability}
                    className="w-full bg-black text-white py-4 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-gray-800 transition-all shadow-lg active:scale-95"
                  >
                    Check Deniz' Agenda
                  </button>
                </div>
              )}

              {reservationStatus === 'checking' && (
                <div className="py-10 flex flex-col items-center">
                  <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mb-4" />
                  <p className="text-lg font-bold text-gray-700 italic">Deniz loopt naar de kalender...</p>
                </div>
              )}

              {reservationStatus === 'rejected' && rejectionData && (
                <div className="space-y-6 text-left">
                  <div className="bg-red-50 border-2 border-red-100 p-6 rounded-3xl">
                    <h4 className="text-red-600 font-black uppercase text-xs tracking-widest mb-2 flex items-center">
                      <XCircle className="w-4 h-4 mr-1" /> Geen match
                    </h4>
                    <p className="text-gray-800 font-bold leading-relaxed italic">"{rejectionData.reason}"</p>
                  </div>
                  <button 
                    onClick={() => setReservationStatus('picking')}
                    className="w-full border-2 border-gray-100 py-3 rounded-2xl font-bold text-gray-500 hover:bg-gray-50 transition-all text-sm"
                  >
                    Andere datum proberen
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-16 w-full flex flex-col items-center">
          <p className="text-[9px] md:text-[10px] text-gray-300 uppercase tracking-[0.4em] mb-8 font-black">Powered by Local Legends</p>
          <GTAAds />
        </div>
      </div>
    </div>
  );
};

export default ResultView;
